import moment from 'moment'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { convertHexToRgba } from '../../../../../utility/stylesUtility'
import { Line } from 'react-chartjs-2'
import { CategoryScale, Chart as ChartJS, Filler, Legend, LinearScale, LineElement, PointElement, Tooltip } from 'chart.js'

import businessDashboardClasses from '../BusinessDashboard.module.scss'
import chartTooltipClasses from '../ChartTooltip.module.scss'
import classes from './ProgramsEnrollmentsActivityWidget.module.scss'
import globals from '../../../../../assets/styles/globals.scss'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, Legend)

Tooltip.positioners.cursor = function(elements, coordinates) {
  return { x: coordinates.x, y: coordinates.y }
}

const buildTooltipHtml = (monthData, currentYear, communityCategories, totalEnrollmentsLabel) => {
  const date = moment.utc(monthData.month)
  const title = date.year() === currentYear
    ? date.format('MMMM')
    : date.format('MMMM YYYY')

  const categories = monthData.courseCategories
  const total = categories.reduce((s, c) => s + (c.value || 0), 0)

  let innerHtml = `
    <div style="font-weight:800; font-size: 1rem; color:${globals.darkNavy}; margin-bottom:1rem;">
      ${title}
    </div>
  `

  categories.forEach(cat => {
    if (!cat) {
      return
    }

    const percentage = total ? Math.round((cat.value / total) * 100) : 0
    const category = communityCategories.find(c => c.id === cat.id)

    innerHtml += `
      <div style="
        display:grid;
        grid-template-columns: 1fr 60px 60px;
        column-gap: 12px;
        align-items:center;
        margin-bottom:4px;
        font-size: 0.9rem;
      ">
        <div style="display:flex; align-items:center; gap:0.75rem;">
          <span
            style="
              display:inline-block;
              width:8px; height:8px;
              border-radius:50%;
              background:${category.color};
              flex-shrink:0;
            "
          ></span>
          ${category.name}
        </div>

        <span style="text-align:right;">${percentage}%</span>
        <span style="text-align:right;font-weight:800;">${cat.value}</span>
      </div>
    `
  })

  innerHtml += `
    <div style='display:flex; justify-content:space-between; font-weight:800; font-size: 0.9rem; color:${globals.darkNavy}; margin-top:1rem;'>
      <span>${totalEnrollmentsLabel}</span>
      <span style='flex-shrink: 0; margin-left: 1rem;'>${total}</span>
    </div>
  `

  return innerHtml
}

const todayLinePlugin = {
  id: 'todayLine',
  afterDraw(chart) {
    const { todayIndex, dayProgress, label } = chart.options.plugins.todayLine || {}
    if (todayIndex === null || todayIndex === undefined || todayIndex < 0) {
      return
    }
    const xScale = chart.scales.x
    const xStart = xScale.getPixelForTick(todayIndex)
    const xEnd = todayIndex < xScale.ticks.length - 1
      ? xScale.getPixelForTick(todayIndex + 1)
      : xStart
    const x = xStart + (xEnd - xStart) * (dayProgress || 0)
    const { top } = chart.chartArea
    const { ctx } = chart

    ctx.save()
    ctx.beginPath()
    ctx.setLineDash([4, 4])
    ctx.strokeStyle = globals.primaryOrange
    ctx.lineWidth = 1.5
    ctx.moveTo(x, top)
    ctx.lineTo(x, chart.chartArea.bottom)
    ctx.stroke()
    ctx.setLineDash([])

    ctx.font = `800 ${window.innerWidth >= 1400 ? '0.9rem' : '0.8rem'} sans-serif`
    ctx.fillStyle = globals.primaryOrange
    ctx.textAlign = 'center'
    ctx.fillText(label, x, top - 8)
    ctx.restore()
  },
}

ChartJS.register(todayLinePlugin)

const ProgramsEnrollmentsLineChart = ({ monthlyData }) => {
  const { t } = useTranslation()
  const communityCategories = useSelector(state => state.dashboard.communityCategories)
  const currentYear = new Date().getFullYear()
  const currentMonth = new Date().getMonth()
  const chartRef = useRef(null)
  const [tickPositions, setTickPositions] = useState([])

  const updateTickPositions = useCallback((chartArg) => {
    const chart = chartArg?.chart || chartArg || chartRef.current
    if (!chart?.scales?.x) {
      return
    }
    const xScale = chart.scales.x
    const positions = monthlyData.map((_, i) => (
      xScale.getPixelForTick(i)
    ))
    setTickPositions(positions)
  }, [monthlyData])

  useEffect(() => {
    const chart = chartRef.current
    if (!chart) {
      return
    }

    updateTickPositions(chart)

    const observer = new ResizeObserver(() => {
      updateTickPositions(chartRef.current)
    })
    observer.observe(chart.canvas)
    return () => observer.disconnect()
  }, [updateTickPositions])

  const labels = useMemo(() => monthlyData.map(item => {
    const date = moment.utc(item.month)
    return date.year() !== currentYear ? date.format('MMM YYYY') : date.format('MMM')
  }), [currentYear, monthlyData])

  const values = monthlyData.map(item => item.value)

  const programsPerMonth = useMemo(() => monthlyData.map(item => item.coursesCount), [monthlyData])

  const todayLineConfig = useMemo(() => {
    const label = t('dashboard.business.programsEnrollmentsActivity.programsEnrollmentsLineChart.today')
    const todayIndex = monthlyData.findIndex(item => {
      const d = moment.utc(item.month)
      return d.year() === currentYear && d.month() === currentMonth
    })
    if (todayIndex < 0) {
      return { todayIndex, label }
    }
    const today = new Date()
    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate()
    return { todayIndex, dayProgress: (today.getDate() - 1) / daysInMonth, label }
  }, [monthlyData, currentYear, currentMonth, t])

  const externalTooltipHandler = useCallback(context => {
    let tooltipEl = document.getElementById('programs-enrollments-tooltip')

    if (!tooltipEl) {
      tooltipEl = document.createElement('div')
      tooltipEl.id = 'programs-enrollments-tooltip'
      tooltipEl.className = chartTooltipClasses.tooltip
      document.body.appendChild(tooltipEl)
    }

    const tooltipModel = context.tooltip

    if (tooltipModel.opacity === 0) {
      tooltipEl.style.opacity = 0
      return
    }

    const dataIndex = tooltipModel.dataPoints?.[0]?.dataIndex

    if (dataIndex === undefined) {
      tooltipEl.style.opacity = 0
      return
    }

    tooltipEl.innerHTML = buildTooltipHtml(
      monthlyData[dataIndex],
      currentYear,
      communityCategories,
      t('dashboard.business.programsEnrollmentsActivity.programsEnrollmentsLineChart.totalEnrollments')
    )

    const canvasRect = context.chart.canvas.getBoundingClientRect()
    tooltipEl.style.opacity = 1

    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight

    let left = canvasRect.left + window.pageXOffset + tooltipModel.caretX + 12
    let top = canvasRect.top + window.pageYOffset + tooltipModel.caretY + 12

    const tooltipRect = tooltipEl.getBoundingClientRect()
    if (left + tooltipRect.width > viewportWidth) {
      left = viewportWidth - tooltipRect.width - 8
    }
    if (top + tooltipRect.height > viewportHeight) {
      top = viewportHeight - tooltipRect.height - 8
    }

    tooltipEl.style.left = left + 'px'
    tooltipEl.style.top = top + 'px'
  }, [communityCategories, currentYear, monthlyData, t])

  const chartData = useMemo(() => ({
    labels,
    datasets: [{
      label: t('dashboard.business.programsEnrollmentsActivity.programsEnrollmentsLineChart.enrollments'),
      data: values,
      borderColor: globals.primaryOrange,
      backgroundColor: convertHexToRgba(globals.primaryOrange, 0.25),
      fill: true,
      tension: 0.35,
      pointRadius: 0,
      pointHoverRadius: 4,
      pointHoverBackgroundColor: globals.primaryOrange,
      pointHoverBorderColor: globals.primaryOrange,
      borderWidth: 3,
      spanGaps: false,
    }],
  }), [labels, values, t])

  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: { top: 20 },
    },
    animation: {
      onComplete: updateTickPositions,
    },
    onResize: updateTickPositions,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
        external: externalTooltipHandler,
        position: 'cursor',
        mode: 'index',
        intersect: false,
      },
      todayLine: todayLineConfig,
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: globals.slateGray,
          maxRotation: 0,
        },
      },
      y: {
        beginAtZero: true,
        ticks: {
          precision: 0,
          maxTicksLimit: 5,
          color: globals.primaryOrange,
        },
        grid: {
          color: globals.slateGrayIcons,
          borderDash: [2, 4],
          drawTicks: false,
        },
        border: {
          display: false,
        },
      },
    },
  }), [externalTooltipHandler, updateTickPositions, todayLineConfig])

  return (
    <div className={classnames('h-100 w-100 d-flex m-0', businessDashboardClasses.sectionWrapper)}>
      <div className='h-100 d-flex flex-column flex-grow-1 min-w-0'>
        <div className={classnames('d-flex flex-column', classes.chartWrapper)}>
          <span className={classes.enrollmentsLabel}>
            {t('dashboard.business.programsEnrollmentsActivity.programsEnrollmentsLineChart.enrollments')}
          </span>
          <div className='flex-grow-1 min-h-0'>
            <Line ref={chartRef} data={chartData} options={options} />
          </div>
        </div>
        <div className={classes.programsCountListWrapper}>
          {programsPerMonth.map((count, index) => (
            <span
              key={monthlyData[index].month}
              className={classes.monthlyProgramsCount}
              style={{
                position: 'absolute',
                left: tickPositions[index],
                transform: 'translateX(-50%)',
              }}
            >
              {count}
            </span>
          ))}
        </div>
      </div>
      <div className='flex-grow-1 d-flex align-items-end'>
        <span className={classnames('pl-3 d-flex align-items-start', classes.programsLabel)}>
          {t('dashboard.business.programsEnrollmentsActivity.programsEnrollmentsLineChart.programs')}
        </span>
      </div>
    </div>
  )
}

ProgramsEnrollmentsLineChart.propTypes = {
  monthlyData: PropTypes.arrayOf(PropTypes.shape({
    month: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]).isRequired,
    value: PropTypes.number,
    courseCategories: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      value: PropTypes.number,
    })),
  })).isRequired,
}

export default ProgramsEnrollmentsLineChart
